import React from "react";
import { Box, Typography, Alert } from "@mui/material";
import FileUploadZone from "./FileUploadZone";
import FilePreviewCard from "./FilePreviewCard";

export default function QuestionCard({
  question,
  uploadState,
  onFileSelect,
  onDelete,
  onView,
  onRetry,
  disabled,
}) {
  const { file, status, fileUrl, error } = uploadState ?? {};

  const isImageQuestion =
    typeof question.text === "string" &&
    (question.text.startsWith("data:image") ||
      question.text.startsWith("http") ||
      question.text.match(/\.(png|jpg|jpeg|gif|webp|svg)$/i));

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        p: { xs: 1.5, sm: 2 },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: { xs: 1.5, sm: 2 },
        mb: 1.5,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}
    >
      {/* ── Column 1: question number ───────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          width: { sm: "48px" },
          textAlign: { xs: "left", sm: "center" },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: "bold",
            color: "#1e1e1e",
          }}
        >
          Q.{question.questionNo}
        </Typography>
      </Box>

      {/* ── Column 2: question content ──────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          maxHeight: { sm: "90px" },
          overflowY: { sm: "auto" },
        }}
      >
        {isImageQuestion ? (
          <Box
            component="img"
            src={question.text}
            alt={`Question ${question.questionNo}`}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: "220px", sm: "80px" },
              objectFit: "contain",
              objectPosition: "left center",
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px" },
              color: "#1e1e1e",
              lineHeight: 1.5,
            }}
          >
            {question.text}
          </Typography>
        )}
      </Box>

      {/* ── Column 3: file upload ────────────────────────────────────── */}
      <Box sx={{ flexShrink: 0 }}>
        {error && !file && (
          <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: "13px" }}>
            {error}
          </Alert>
        )}

        {!file ? (
          <FileUploadZone
            onFileSelect={(selectedFile, validationError) =>
              onFileSelect(question.id, selectedFile, validationError)
            }
            disabled={disabled}
          />
        ) : (
          <FilePreviewCard
            file={file}
            status={status}
            fileUrl={fileUrl}
            onDelete={() => onDelete(question.id)}
            onView={() => onView(fileUrl)}
            onRetry={() => onRetry(question.id)}
            disabled={disabled}
          />
        )}
      </Box>
    </Box>
  );
}
